import { RouteRecordRaw } from 'vue-router';
import { storeToRefs } from 'pinia';
import pinia from '@/stores/index';
import { useRequestOldRoutes } from '@/stores/requestOldRoutes';
import { NextLoading } from '@/utils/loading';
import { dynamicRoutes, notFoundAndNoPower } from '@/router/route';
import { formatTwoStageRoutes, formatFlatteningRoutes, router } from '@/router/index';
import { useRoutesList } from '@/stores/routesList';
import { useTagsViewRoutes } from '@/stores/tagsViewRoutes';
import {store} from '@/store/store';
import {getmenu,UserParams} from '@/services/api';
import dynamicViewsModules from './zmodule';
import { getjson } from '@/utils/getjson';
import { defineAsyncComponent } from 'vue';
const parent = defineAsyncComponent(() => import('@/layout/routerView/parent.vue'));
const content = defineAsyncComponent(() => import('@/layout/routerView/content.vue'));
/**
 * 获取目录下的 .vue、.tsx 全部文件
 * @method import.meta.glob
 * @link 参考：https://cn.vitejs.dev/guide/features.html#json
 */

/**
 * 后端控制路由：初始化方法，防止刷新时路由丢失
 * @method NextLoading 界面 loading 动画开始执行
 * @method useUserInfo().setUserInfos() 触发初始化用户信息 pinia
 * @method useRequestOldRoutes().setRequestOldRoutes() 存储接口原始路由（未处理component），根据需求选择使用
 * @method setAddRoute 添加动态路由
 * @method setFilterMenuAndCacheTagsViewRoutes 设置路由到 pinia routesList 中（已处理成多级嵌套路由）及缓存多级嵌套数组处理后的一维数组
 */
export async function initBackEndControlRoutes() {
	// 界面 loading 动画开始执行
	if (window.nextLoading === undefined) NextLoading.start();
	// 无 token 停止执行下一步
	// 触发初始化用户信息 pinia
	// https://gitee.com/lyt-top/vue-next-admin/issues/I5F1HP
	// 获取路由菜单数据	
	if (!store.state.app.menudata){
		let res;
		if (!store.state.config.dyroute==null||store.state.config.dyroute){
			res= await getBackEndControlRoutes();			 
			store.state.app.menudata=res.data;		 
		  sessionStorage.setItem('dc-menu', JSON.stringify(res.data));	
		}	 
		else{
	    res=await getjson(store.state.config.crouter); 			 
			store.state.app.menudata=res;		 
		  sessionStorage.setItem('dc-menu', JSON.stringify(res));	
		}		
				
	}		 	
	// 存储接口原始路由（未处理component），根据需求选择使用
	if (store.state.app.menudata.data.length<=0) Promise.resolve(true);
	const cjsonroute=JSON.stringify(store.state.app.menudata.data);
	useRequestOldRoutes().setRequestOldRoutes(JSON.parse(cjsonroute));
	// 处理路由（component），替换 dynamicRoutes（@/router/route）第一个顶级 children 的路由
	dynamicRoutes[0].children =  backEndComponent(JSON.parse(cjsonroute));	
		
	// 添加动态路由
  setAddRoute();
	// 设置路由到 pinia routesList 中（已处理成多级嵌套路由）及缓存多级嵌套数组处理后的一维数组
	setFilterMenuAndCacheTagsViewRoutes();	
	store.state.app.bmenuloaded=true;			
}

/**
 * 设置路由到 pinia routesList 中（已处理成多级嵌套路由）及缓存多级嵌套数组处理后的一维数组
 * @description 用于左侧菜单、横向菜单的显示
 * @description 用于 tagsView、菜单搜索中：未过滤隐藏的(isHide)
 */
export async function setFilterMenuAndCacheTagsViewRoutes() {
	const storesRoutesList = useRoutesList(pinia);
	storesRoutesList.setRoutesList(dynamicRoutes[0].children as any);
	setCacheTagsViewRoutes();
}

/**
 * 缓存多级嵌套数组处理后的一维数组
 * @description 用于 tagsView、菜单搜索中：未过滤隐藏的(isHide)
 */
export function setCacheTagsViewRoutes() {
	const storesTagsView = useTagsViewRoutes(pinia);
	storesTagsView.setTagsViewRoutes(formatTwoStageRoutes(formatFlatteningRoutes(dynamicRoutes))[0].children);
}

/**
 * 处理路由格式及添加捕获所有路由或 404 Not found 路由
 * @description 替换 dynamicRoutes（@/router/route）第一个顶级 children 的路由
 * @returns 返回替换后的路由数组
 */
export function setFilterRouteEnd() {
	let filterRouteEnd: any = formatTwoStageRoutes(formatFlatteningRoutes(dynamicRoutes));
	// notFoundAndNoPower 防止 404、401 不在 layout 布局中，不设置的话，404、401 界面将全屏显示
	// 关联问题 No match found for location with path 'xxx'
	filterRouteEnd[0].children = [...filterRouteEnd[0].children, ...notFoundAndNoPower];
	return filterRouteEnd;
}

/**
 * 添加动态路由
 * @method router.addRoute
 * @description 此处循环为 dynamicRoutes（@/router/route）第一个顶级 children 的路由一维数组，非多级嵌套
 * @link 参考：https://next.router.vuejs.org/zh/api/#addroute
 */
export async function setAddRoute() {
	await setFilterRouteEnd().forEach((route: RouteRecordRaw) => {
		router.addRoute(route);
	});
}

/**
 * 请求后端路由菜单接口
 * @description isRequestRoutes 为 true，则开启后端控制路由
 * @returns 返回后端路由菜单数据
 */
export function getBackEndControlRoutes() {
	const para:UserParams={'iduser':store.state.user.iduser};	 
	return getmenu(para)
}

/**
 * 重新请求后端路由菜单接口
 * @description 用于菜单管理界面刷新菜单（未进行测试）
 * @description 路径：/src/views/system/menu/component/addMenu.vue
 */
export async function setBackEndControlRefreshRoutes() {
	await getBackEndControlRoutes();
}

/**
 * 后端路由 component 转换
 * @param routes 后端返回的路由表数组
 * @returns 返回处理成函数后的 component
 */
export function backEndComponent(routes: any) {
	if (!routes) return;	
  return routes.map((item: any) => {	
    if (item.type === 0)
      item.component = parent;
    else {
      if (!item.component.startsWith('/home/index'))
        item.component = content;
      else {
        item.component = dynamicImport(dynamicViewsModules,'/views'+item.component as string);
      }        
    }
		// if (typeof(item.component)=='string') {
		// 	if (item.component.startsWith('/layout'))					   
		// 	   //item.component = loadView(item.component as string);	
		// 	  item.component = dynamicImport(dynamicViewsModules, item.component as string);
		// 	else
		// 	  //item.component = loadView('/views'+item.component as string);	
		// 	  item.component = dynamicImport(dynamicViewsModules,'/views'+item.component as string);
		// }				 
		item.children && backEndComponent(item.children);
		return item;
	});
}

/**
 * 后端路由 component 转换函数
 * @param dynamicViewsModules 获取目录下的 .vue、.tsx 全部文件
 * @param component 当前要处理项 component
 * @returns 返回处理成函数后的 component
 */
export function dynamicImport(dynamicViewsModules: Record<string, Function>, component: string) {		 
	const keys:any = dynamicViewsModules[`..${component}.vue`];		 	
	if (keys) {		 
		return keys;
	}
	else {
		return false;
	}
} 
export function getPathByName(name: string) {		
    if (!store.state.app.menudata||!store.state.app.menudata.data||store.state.app.menudata.data.length===0)
	   return '';
	let menulst= store.state.app.menudata.data;
	 for(let i1=0;i1<menulst.length;i1++){
         if (menulst[i1].name===name)
                 return menulst[i1].path;
	 }
} 
export function dynamicLoad(component: string) {	
  console.log('comonent.....',`..${component}.vue`)
  return dynamicViewsModules[`..${component}.vue`];	
} 
